import React from "react";
import sys from "system-components";
import { Box, Flex } from "grid-styled";

export const Heading = sys(
    {
        is: "h2",
        fontSize: 5,
        fontWeight: "bold",
        lineHeight: 1.25,
        m: 0
    },
    "color",
    "lineHeight",
    "textAlign",
    "maxWidth"
);

Heading.displayName = "Heading";

Heading.h1 = props => <Heading {...props} is="h1" />;
Heading.h2 = props => <Heading {...props} is="h2" />;
Heading.h3 = props => <Heading {...props} is="h3" />;
Heading.h4 = props => <Heading {...props} is="h4" />;
Heading.h5 = props => <Heading {...props} is="h5" />;
Heading.h6 = props => <Heading {...props} is="h6" />;

export const Container = sys(
    {
        is: Box,
        px: 6,
        mx: "auto",
        maxWidth: 1024
    },
    "space",
    "maxWidth"
);

Container.displayName = "Container";

export const Text = sys(
    {
        m: 0
    },
    "space",
    "color",
    "fontSize",
    "fontWeight",
    "textAlign",
    "lineHeight",
    "fontFamily",
    "boxShadow"
);

Text.displayName = "Text";

export const Measure = sys(
    {
        is: Text,
        maxWidth: "32em"
    },
    "space",
    "maxWidth",
    "textAlign",
    "fontSize",
    "color"
);

Measure.displayName = "Measure";

export const Avatar = sys(
    {
        is: "img",
        size: 48,
        borderRadius: "50%"
    },
    {
        display: "inline-block"
    },
    "space",
    "color",
    "size",
    "borders"
);

Avatar.displayName = "Avatar";

export const FlexFullViewport = sys({
    is: Flex,
    height: "100vh",
    width: "100vw"
});

const Position = sys(
    "space",
    "color",
    "zIndex",
    "top",
    "right",
    "bottom",
    "left"
);

Position.displayName = "Position";

export const Relative = sys(
    {
        is: Position
    },
    "height",
    {
        position: "relative"
    }
);

Relative.displayName = "Relative";

export const Absolute = sys(
    {
        is: Position
    },
    "color",
    "borders",
    "height",
    {
        position: "absolute"
    }
);

export const Fixed = sys(
    {
        is: Position
    },
    "borders",
    {
        position: "fixed"
    }
);
Fixed.displayName = "Fixed";

Absolute.displayName = "Absolute";

export const Card = sys(
    {
        bg: "white",
        borderRadius: 4,
        boxShadow:
            "0 1px 0 rgba(0, 0, 0, .05), 0 1px 2px rgba(0, 0, 0, .05), 0 4px 16px rgba(0, 0 , 0, .05)"
    },
    {
        overflow: "hidden"
    },
    "space",
    "color"
);

Card.displayName = "Card";

export const Divider = sys(
    {
        is: "hr",
        mx: 0,
        my: 0,
        border: 0,
        borderBottom: 1,
        borderColor: "#EBEBEB"
    },
    "space",
    "color"
);

Divider.displayName = "Divider";

export const Clickable = sys(
    {
        is: Box
    },
    "hover",
    {
        cursor: "pointer"
    }
);

export const Badge = sys(
    {
        fontSize: 1,
        color: "white",
        bg: "blue",
        fontWeight: "bold",
        borderRadius: 4
    },
    {
        display: "inline-block",
        verticalAlign: "middle"
    },
    "space"
);

Badge.displayName = "Badge";

export const Circle = sys(
    {
        is: Flex,
        size: 24,
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        borderRadius: "50%",
        fontSize: 0,
        bg: "red"
    },
    "size",
    "borders"
);

Circle.displayName = "Circle";

export const ScrollView = sys(
    {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%"
    },
    "space",
    {
        overflowY: "auto"
    }
);

ScrollView.displayName = "ScrollView";

export const RoundedFlex = sys(
    {
        is: Flex,
        borderRadius: 4
    },
    "borderRadius",
    "borders",
    "height"
);

RoundedFlex.displayName = "RoundedFlex";

export const ElevatedBox = sys(
    {
        is: Box
    },
    "boxShadow"
);

ElevatedBox.displayName = "ElevatedBox";

export const SlimBox = sys({
    is: Box,
    lineHeight: 1
});

export const FlexWithHeight = sys(
    {
        is: Flex
    },
    "height"
);

export const BoxWithHeight = sys(
    {
        is: Box
    },
    "height",
    "borders"
);

BoxWithHeight.displayName = "BoxWithHeight";

export const ElevatedFlex = sys(
    {
        is: Flex
    },
    "boxShadow"
);

ElevatedFlex.displayName = "ElevatedFlex";

export const FlexWithMinHeight = sys(
    {
        is: Flex
    },
    "minHeight"
);

FlexWithMinHeight.displayName = "FlexWithMinHeight";

export const TextLink = sys(
    {
        is: "a",
        fontWeight: "normal"
    },
    {
        transition: "color .2s ease"
    },
    "lineHeight",
    "fontWeight",
    "textAlign",
    "fontSize",
    "color",
    props => ({
        color: props.shade === "light" ? "#3D3D3D" : "currentColor",
        "&:hover": {
            color: props.shade === "light" ? "#3429FF" : "#3429FF"
        }
    })
);

export const Button = sys(
    {
        is: "button",
        fontSize: 3,
        fontWeight: "bold",
        lineHeight: 1,
        m: 0,
        px: 6,
        py: 4,
        color: "white",
        bg: "blue",
        borderRadius: 4,
        border: 0
    },
    props => ({
        fontFamily: "inherit",
        cursor: "pointer",
        WebkitFontSmoothing: "antialiased",
        display: "inline-block",
        verticalAlign: "middle",
        textAlign: "center",
        textDecoration: "none",
        appearance: "none",
        "&:disabled": {
            opacity: 1 / 4
        }
    }),
    props => ({
        "&:focus": {
            outline: "none"
        }
    })
);

Button.displayName = "Button";

export const Highlight = sys({
    is: Text,
    boxShadow: "inset 0 -16px 0 0px cyan"
});

Highlight.displayName = "Highlight";

export const SlimLink = sys({
    is: TextLink,
    lineHeight: 1
});

export const Image = sys(
    {
        is: "img"
    },
    {
        display: "block",
        maxWidth: "100%",
        height: "auto"
    },
    "space",
    "width",
    "color"
);

Image.displayName = "Image";

export default Image;



// WEBPACK FOOTER //
// ./src/components/globals/index.js